import { formatDate } from '@angular/common';
import { createSelector } from '@ngrx/store';

import { AttachmentGroup, YesNoIDK } from '@features/succession/enums';
import {
  ConfirmationInformation,
  DeceasedInformationSummary,
  DocumentInformationSummary,
  DocumentSummary,
  InheritanceInformationSummary,
  ReporterInformationSummary,
  StandardInformation,
  SummaryInformation,
} from '@features/succession/interfaces/summary-information.interface';
import {
  DeceasedInformation,
  DeceasedMoreInformation,
  DocumentItem,
  Inheritance,
  InheritanceInformation,
  ListBoxItem,
  ListBoxItems,
  Notary,
  NotaryInformation,
  ReporterInformation,
  TypeDocument,
} from '@features/succession/models';

import {
  selectDeceasedInformation,
  selectDeceasedMoreInformation,
  selectInheritanceInformation,
  selectListBoxItems,
  selectNotaryInformation,
  selectReporterInformation,
  selectSelfCareTypeDocuments,
  selectToken,
} from './customer-information.selector';

function getLabelFromListBoxItem(listBox?: ListBoxItem[], value?: string): string {
  return listBox?.find((list) => list.value === value)?.label || '';
}

function mapFullAddress(address?: string, zipcode?: string, city?: string): string {
  const zipcodeParsed = zipcode ? `, ${zipcode}` : '';
  const cityParsed = city ? ` ${city}` : '';
  return address + zipcodeParsed + cityParsed;
}

function createStandardInformationObject(
  information?: Inheritance | Notary,
): StandardInformation | undefined {
  return information?.name?.length && information?.firstname.length
    ? {
        fullName: `${information?.name} ${information?.firstname}`,
        fullAddress: mapFullAddress(information?.address, information?.zipcode, information?.city),
        email: information?.email,
        phoneNumber: information?.phoneNumber,
      }
    : undefined;
}

function mapDocumentSummary(
  documents?: DocumentItem[],
  token?: string,
  typeDocuments?: TypeDocument[],
): DocumentSummary[] | undefined {
  return documents?.map((doc) => {
    const documentTyped = typeDocuments?.find((type) => type.typeDocument === doc.typeDocument);
    return {
      key: parseInt(documentTyped?.attachmentGroup || ''),
      filename: `${documentTyped?.filename}.${doc.type}`,
      label: documentTyped?.isMultiple
        ? `${documentTyped.filename} (${doc.filename}.${doc.type})`
        : documentTyped?.filename,
      link: `/rgg/inheritances/v1/inheritances/documents/${token}/${doc.referenceId}?encoded=false`,
    };
  });
}

function mapDeceasedInformation(deceasedInformation?: DeceasedInformation): string | undefined {
  let gender = undefined;
  if (deceasedInformation?.gender) {
    gender = deceasedInformation?.gender === 'WOMAN' ? 'Mme. ' : 'M. ';
  }
  const fullName =
    deceasedInformation?.firstname && deceasedInformation?.name
      ? `${deceasedInformation?.firstname} ${deceasedInformation?.name}`
      : undefined;
  return gender && fullName ? gender + fullName : undefined;
}

export const selectRelationshipWithDeceased = createSelector(
  selectListBoxItems,
  (listBoxItems?: ListBoxItems) => listBoxItems?.relationshipWithDeceasedList,
);

export const selectMaritalStatus = createSelector(
  selectListBoxItems,
  (listBoxItems?: ListBoxItems) => listBoxItems?.maritalStatus,
);

export const selectCountries = createSelector(
  selectListBoxItems,
  (listBoxItems?: ListBoxItems): ListBoxItem[] => listBoxItems?.countries || [],
);

export const selectGenders = createSelector(
  selectListBoxItems,
  (listBoxItems?: ListBoxItems) => listBoxItems?.gender,
);

export const selectDeceasedInformationSummary = createSelector(
  selectDeceasedInformation,
  selectMaritalStatus,
  selectDeceasedMoreInformation,
  selectCountries,
  (
    deceasedInformation?: DeceasedInformation,
    maritalStatus?: ListBoxItem[],
    deceasedMoreInformation?: DeceasedMoreInformation,
    countries?: ListBoxItem[],
  ): DeceasedInformationSummary => ({
    fullName: `${deceasedInformation?.name} ${deceasedInformation?.firstname}`,
    fullBirthday: [
      `Le ${formatDate(deceasedInformation?.dateOfBirth || '', 'dd/MM/yyyy', 'fr')}`,
      deceasedInformation?.communeOfBirth ? `à ${deceasedInformation?.communeOfBirth}` : '',
      getLabelFromListBoxItem(countries, deceasedInformation?.countryOfBirth),
    ]
      .filter((i) => i)
      .join(', '),
    fullAddress: mapFullAddress(
      deceasedInformation?.address,
      deceasedInformation?.zipcode,
      deceasedInformation?.city,
    ),
    maritalStatus: getLabelFromListBoxItem(maritalStatus, deceasedInformation?.maritalStatus),
    deceasedDate: `${formatDate(deceasedMoreInformation?.deceasedDate || '', 'dd/MM/yyyy', 'fr')}`,
    iban: deceasedInformation?.iban?.replace(/(.{0,9})(.+)/, '$1 **** **** **** **** **'),
    funeralExpenses: deceasedMoreInformation?.hasFuneralContract === 'YES' ? 'Oui' : 'Non',
  }),
);

export const selectReporterInformationSummary = createSelector(
  selectReporterInformation,
  selectRelationshipWithDeceased,
  (
    reporterInformation?: ReporterInformation,
    relationshipWithDeceased?: ListBoxItem[],
  ): ReporterInformationSummary => ({
    fullName: `${reporterInformation?.name} ${reporterInformation?.firstname}`,
    relationshipWithDeceased: getLabelFromListBoxItem(
      relationshipWithDeceased,
      reporterInformation?.relationshipWithDeceased,
    ),
    fullAddress: mapFullAddress(
      reporterInformation?.address,
      reporterInformation?.zipcode,
      reporterInformation?.city,
    ),
    email: reporterInformation?.email,
    phoneNumber: reporterInformation?.phoneNumber,
  }),
);

export const selectNotaryInformationSummary = createSelector(
  selectNotaryInformation,
  (notaryInformation?: NotaryInformation) =>
    createStandardInformationObject(notaryInformation?.notary),
);

export const selectHasNotary = createSelector(
  selectNotaryInformation,
  (notaryInformation?: NotaryInformation): boolean => notaryInformation?.hasNotary === YesNoIDK.YES,
);

export const selectInheritanceInformationSummary = createSelector(
  selectInheritanceInformation,
  (inheritanceInformation?: InheritanceInformation): InheritanceInformationSummary | undefined => {
    const spouse = inheritanceInformation?.spouse;
    const children = inheritanceInformation?.children || [];
    if (spouse?.name || children.length) {
      return {
        spouse: createStandardInformationObject(spouse),
        children: [...children]
          .sort((a: Inheritance, b: Inheritance) => {
            if (!a.id || !b.id) {
              return 0;
            }
            return Number(b.id) - Number(a.id);
          })
          .map((child: Inheritance) => createStandardInformationObject(child))
          .filter((item?: StandardInformation): item is StandardInformation => !!item),
      };
    }
    return undefined;
  },
);

export const selectDocumentsSummary = createSelector(
  selectDeceasedMoreInformation,
  selectToken,
  selectSelfCareTypeDocuments,
  (
    deceasedMoreInformation?: DeceasedMoreInformation,
    token?: string,
    typeDocuments?: TypeDocument[],
  ): DocumentInformationSummary => {
    const documents = deceasedMoreInformation?.documents;
    const deathCertificateDocument = documents?.filter(
      (doc) => doc.uploadedAttachmentGroup === AttachmentGroup.DEATH_CERTIFICATE,
    );
    const otherDocuments = documents?.filter((doc) => parseInt(doc.uploadedAttachmentGroup) === 2);
    const funeralExpenses = documents?.filter((doc) => parseInt(doc.uploadedAttachmentGroup) > 2);

    return {
      deathCertificateDocument: mapDocumentSummary(deathCertificateDocument, token, typeDocuments),
      otherDocuments: mapDocumentSummary(otherDocuments, token, typeDocuments),
      funeralExpenses: mapDocumentSummary(funeralExpenses, token, typeDocuments),
    };
  },
);

export const selectSummaryInformation = createSelector(
  selectHasNotary,
  selectDeceasedInformationSummary,
  selectReporterInformationSummary,
  selectNotaryInformationSummary,
  selectInheritanceInformationSummary,
  selectDocumentsSummary,
  (
    hasNotary: boolean,
    deceasedInformation: DeceasedInformationSummary,
    reporterInformation?: ReporterInformationSummary,
    notaryInformation?: StandardInformation,
    inheritanceInformation?: InheritanceInformationSummary,
    documents?: DocumentInformationSummary,
  ): SummaryInformation => ({
    deceasedInformation,
    reporterInformation,
    notaryInformation,
    inheritanceInformation,
    documents,
    hasNotary,
  }),
);

export const selectConfirmationInformation = createSelector(
  selectDeceasedInformation,
  selectReporterInformationSummary,
  (
    deceasedInformation?: DeceasedInformation,
    reporterInformation?: ReporterInformationSummary,
  ): ConfirmationInformation => ({
    deceasedInformation: mapDeceasedInformation(deceasedInformation),
    reporterEmail: reporterInformation?.email,
  }),
);
